import { api, ErroApi } from "./api";

/**
 * ENVIO DE FOTOS
 *
 * O caminho da foto do celular do mecânico até o storage:
 *
 *   comprimir  →  hash  →  autorizar  →  enviar direto  →  confirmar
 *
 * A API só autoriza. Os bytes vão do navegador para o storage sem passar
 * pelo servidor — que é o que permite subir 20 MB sem travar o sistema para
 * quem está no balcão.
 *
 * ═══ SOBRE O MODO OFFLINE ═══
 *
 * A fila de texto vive em localStorage. Foto não cabe lá: o limite é de
 * poucos megabytes e a API é síncrona, então gravar uma foto travaria a
 * interface. Aqui é IndexedDB, que guarda Blob direto e é assíncrono.
 *
 * Sem isso, o mecânico documenta o carro no fundo do galpão, sai da tela, e
 * o trabalho evapora. Uma vez que isso acontece, ele volta para a prancheta
 * e não tenta de novo.
 */

const BANCO = "bancada.midia";
const LOJA = "fila";

function abrir(): Promise<IDBDatabase> {
  return new Promise((ok, erro) => {
    const req = indexedDB.open(BANCO, 1);
    req.onupgradeneeded = () => {
      const d = req.result;
      if (!d.objectStoreNames.contains(LOJA)) d.createObjectStore(LOJA, { keyPath: "id" });
    };
    req.onsuccess = () => ok(req.result);
    req.onerror = () => erro(req.error);
  });
}

async function transacaoLoja<T>(modo: IDBTransactionMode, fn: (l: IDBObjectStore) => IDBRequest): Promise<T> {
  const d = await abrir();
  return new Promise((ok, erro) => {
    const r = fn(d.transaction(LOJA, modo).objectStore(LOJA));
    r.onsuccess = () => ok(r.result as T);
    r.onerror = () => erro(r.error);
  });
}

export interface FotoPendente {
  id: string;
  blob: Blob;
  hash: string;
  tipo: string;
  checklistId: string | null;
  avariaId: string | null;
  largura: number;
  altura: number;
  criadoEm: string;
  tentativas: number;
}

/* ── preparo ──────────────────────────────────────────────────────────── */

/**
 * Comprime antes de qualquer coisa.
 *
 * Foto de celular tem 3 a 4 MB. A 1600px com qualidade 0,8 ela cai para uns
 * 300 KB — dez vezes menos — e a avaria continua visível, a placa continua
 * legível. É o maior ganho de todo o fluxo, e acontece no aparelho, de graça.
 */
/**
 * Decodifica a imagem.
 *
 * createImageBitmap é o caminho rápido, mas o Safari do iPhone não decodifica
 * HEIC por ele — e HEIC é o formato padrão da câmera da Apple. A foto do
 * mecânico morria aqui, num catch que só escrevia no console.
 *
 * O elemento <img> usa o decodificador do sistema, que abre HEIC. É mais
 * lento, então fica como segunda tentativa e não como caminho principal.
 */
async function decodificar(arquivo: File | Blob): Promise<
  { fonte: CanvasImageSource; w: number; h: number; fechar: () => void }
> {
  try {
    const bmp = await createImageBitmap(arquivo);
    return { fonte: bmp, w: bmp.width, h: bmp.height, fechar: () => bmp.close?.() };
  } catch {
    const url = URL.createObjectURL(arquivo);
    try {
      const el = await new Promise<HTMLImageElement>((ok, falhou) => {
        const i = new Image();
        i.onload = () => ok(i);
        i.onerror = () => falhou(new Error("formato de imagem não suportado neste aparelho"));
        i.src = url;
      });
      return {
        fonte: el, w: el.naturalWidth, h: el.naturalHeight,
        fechar: () => URL.revokeObjectURL(url),
      };
    } catch (e) { URL.revokeObjectURL(url); throw e; }
  }
}

export async function preparar(arquivo: File | Blob, maxLado = 1600, qualidade = 0.8) {
  const img = await decodificar(arquivo);
  const escala = Math.min(1, maxLado / Math.max(img.w, img.h));
  const largura = Math.round(img.w * escala);
  const altura = Math.round(img.h * escala);

  const canvas = document.createElement("canvas");
  canvas.width = largura; canvas.height = altura;
  canvas.getContext("2d")!.drawImage(img.fonte, 0, 0, largura, altura);
  img.fechar();

  const blob: Blob = await new Promise((r) =>
    canvas.toBlob((b) => r(b!), "image/jpeg", qualidade));

  // O hash serve para dedup: a rede oscila, o mecânico tenta de novo, e sem
  // isso a mesma foto vira dois arquivos cobrados.
  const buf = await blob.arrayBuffer();
  const hash = Array.from(new Uint8Array(await crypto.subtle.digest("SHA-256", buf)))
    .map((b) => b.toString(16).padStart(2, "0")).join("");

  return { blob, hash, largura, altura, bytes: blob.size };
}

/* ── envio ────────────────────────────────────────────────────────────── */

export async function enviar(
  foto: { blob: Blob; hash: string; largura: number; altura: number },
  destino: { tipo?: string; checklistId?: string | null; avariaId?: string | null } = {},
): Promise<{ midiaId: string; jaExistia?: boolean }> {
  // 1. autoriza
  const a = await api<any>("/midias", {
    metodo: "POST",
    corpo: {
      hash: foto.hash, bytes: foto.blob.size, mime: "image/jpeg",
      largura: foto.largura, altura: foto.altura,
      tipo: destino.tipo ?? "foto_veiculo",
      checklistId: destino.checklistId ?? null, avariaId: destino.avariaId ?? null,
    },
  });

  // O servidor já tinha essa foto: nada a enviar.
  if (a.jaExistia) return { midiaId: a.midiaId, jaExistia: true };

  /* 2. envia pela nossa própria API, que repassa ao storage.

     O desenho original mandava o navegador direto para a Cloudflare, com
     uma URL assinada. É mais barato — os bytes não passam pelo servidor —
     mas exige CORS no bucket liberando o endereço do site, e o guia de
     instalação deste projeto avisa em letras maiúsculas que sem isso "o
     navegador recusa o envio direto e a foto nunca sai do aparelho —
     falha silenciosa".

     Foi o que aconteceu: zero fotos no banco desde o primeiro dia. E como
     o bloqueio é do navegador, não chega nem a virar log no servidor.

     Pela API é mesma origem (o proxy da Netlify), então não há CORS para
     dar errado. Foto de checklist tem ~300 KB depois da compressão; umas
     dezenas por dia atravessando o servidor não é custo que se perceba, e
     tira uma configuração de painel de terceiro do caminho crítico. */
  await api(`/midias/${a.midiaId}/conteudo`, {
    metodo: "POST", binario: foto.blob, mime: "image/jpeg",
  });
  return { midiaId: a.midiaId };
}

/* ── fila ─────────────────────────────────────────────────────────────── */

export async function enfileirarFoto(
  foto: { blob: Blob; hash: string; largura: number; altura: number },
  destino: { tipo?: string; checklistId?: string | null; avariaId?: string | null } = {},
): Promise<string> {
  const item: FotoPendente = {
    id: crypto.randomUUID(), blob: foto.blob, hash: foto.hash,
    largura: foto.largura, altura: foto.altura,
    tipo: destino.tipo ?? "foto_veiculo",
    checklistId: destino.checklistId ?? null, avariaId: destino.avariaId ?? null,
    criadoEm: new Date().toISOString(), tentativas: 0,
  };
  await transacaoLoja("readwrite", (l) => l.put(item));
  return item.id;
}

export const fotosPendentes = () =>
  transacaoLoja<FotoPendente[]>("readonly", (l) => l.getAll());

/**
 * Sobe o que está na fila.
 *
 * Uma por vez, de propósito: o 4G do galpão engasga com envios paralelos e
 * o resultado é tudo falhando junto em vez de algumas passando.
 */
export async function sincronizarFotos(
  aoProgredir?: (feitas: number, total: number) => void,
): Promise<{ enviadas: number; restantes: number }> {
  const fila = await fotosPendentes();
  if (!fila.length) return { enviadas: 0, restantes: 0 };

  let enviadas = 0;
  for (const f of fila) {
    try {
      await enviar(f, { tipo: f.tipo, checklistId: f.checklistId, avariaId: f.avariaId });
      await transacaoLoja("readwrite", (l) => l.delete(f.id));
      enviadas++;
      aoProgredir?.(enviadas, fila.length);
    } catch (e) {
      // 4xx é problema no dado: insistir não resolve e trava a fila atrás de
      // um item que nunca vai passar. Depois de 5 tentativas, desiste.
      const definitivo = e instanceof ErroApi && e.status >= 400 && e.status < 500;
      if (definitivo || f.tentativas >= 5) {
        console.error("[foto] descartada", f.id, e);
        await transacaoLoja("readwrite", (l) => l.delete(f.id));
      } else {
        await transacaoLoja("readwrite", (l) => l.put({ ...f, tentativas: f.tentativas + 1 }));
      }
    }
  }
  const restantes = (await fotosPendentes()).length;
  return { enviadas, restantes };
}

/** Vincula ao checklist depois que ele ganha id no servidor. */
export async function vincularAoChecklist(checklistId: string): Promise<void> {
  const fila = await fotosPendentes();
  for (const f of fila.filter((x) => !x.checklistId)) {
    await transacaoLoja("readwrite", (l) => l.put({ ...f, checklistId }));
  }
}

export function observarFotos(aoEnviar: (r: { enviadas: number }) => void) {
  const h = async () => {
    if (!navigator.onLine) return;
    const r = await sincronizarFotos().catch(() => ({ enviadas: 0 }));
    if (r.enviadas) aoEnviar(r);
  };
  window.addEventListener("online", h);
  /* Uma vez agora, não daqui a 45 segundos. Quem abre o aplicativo com foto
     presa da vez passada quer ver ela subir, não esperar sem sinal de nada. */
  h();
  const t = setInterval(h, 45_000);
  return () => { window.removeEventListener("online", h); clearInterval(t); };
}
